/** Chip rendered inside the trigger (`.sd-chip`). */
export const SVZ_CHIP_TRIGGER_CHIP_ATTR = 'data-svz-trigger-chip';

/** `+N` pill that collects the chips hidden by `fitTriggerChips` (`.sd-more`). */
export const SVZ_CHIP_TRIGGER_MORE_ATTR = 'data-svz-trigger-more';

/**
 * Prototype droplist `fit()`: the trigger value is a single line (`nowrap` + `overflow:hidden`), so while the chips
 * overflow it, the last ones are hidden one by one and counted in the `+N` pill (its `title` lists who is out).
 * The first chip always stays. Pure DOM work on purpose — it runs after every render and on every resize of the
 * trigger, and must not schedule change detection. Visibility toggles the Tailwind `hidden` class rather than an
 * inline style: the workspace compiles utilities with `important`, so `inline-flex` would beat `style="display:none"`.
 * Returns the labels that were hidden.
 */
export function fitTriggerChips(wrap: HTMLElement): string[] {
  const chips = Array.from(wrap.querySelectorAll<HTMLElement>(`[${SVZ_CHIP_TRIGGER_CHIP_ATTR}]`));
  const more = wrap.querySelector<HTMLElement>(`[${SVZ_CHIP_TRIGGER_MORE_ATTR}]`);

  for (const chip of chips) {
    chip.classList.remove('hidden');
  }
  if (more) {
    more.classList.add('hidden');
    more.textContent = '';
    more.removeAttribute('title');
  }

  if (!wrap.clientWidth || chips.length < 2) {
    return [];
  }

  const hidden: string[] = [];
  for (let index = chips.length - 1; index > 0 && wrap.scrollWidth > wrap.clientWidth + 1; index -= 1) {
    chips[index].classList.add('hidden');
    hidden.unshift(chips[index].textContent?.trim() ?? '');
    if (more) {
      more.classList.remove('hidden');
      more.textContent = `+${hidden.length}`;
      more.title = hidden.join(', ');
    }
  }
  return hidden;
}

/**
 * Keeps a trigger fitted: refits on demand (after each render) and whenever the trigger changes width. One observer
 * per element; `disconnect()` on destroy.
 */
export class SvzChipTriggerFitter {
  private observer?: ResizeObserver;
  private observed?: HTMLElement;

  fit(wrap: HTMLElement | undefined): void {
    if (!wrap) {
      return;
    }
    this.observe(wrap);
    fitTriggerChips(wrap);
  }

  disconnect(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.observed = undefined;
  }

  private observe(wrap: HTMLElement): void {
    if (this.observed === wrap || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.disconnect();
    this.observer = new ResizeObserver(() => fitTriggerChips(wrap));
    this.observer.observe(wrap);
    this.observed = wrap;
  }
}
